import {
  createFilingDocument,
  FilingDocumentTypeEnum,
  SalesTaxFiling,
  submitSalesTaxFiling,
  updateSalesTaxFiling,
  useFilingPrepaymentList,
  useSalesTaxFilingDocuments,
} from '@commenda/api';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
  InputErrorMessage,
  SingleFileUploader,
  TimelineLegacy,
  TTimelineItemStatusLegacy,
  uploadFileToS3,
  useTimelineLegacy,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { FileBasic } from '@commenda/types/src/file/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { LegacyDocumentType } from '@prisma/client';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useNotification } from '../../../../hooks/useNotification';
import { Filing } from '../../../../molecules';
import FilePreviewModal from '../../../../molecules/filings/overview/FilePreviewModal';
import FilingInputRow from '../../../../sales-tax/filing/filing-review/FilingInputRow';
import {
  FILING_CONFIRMATION_FILE_CONFIG,
  PAYMENT_CONFIRMATION_FILE_CONFIG,
} from '../../constants';
import PrepaymentLogTable from './PrepaymentLogTable';

enum FilingCompletionSteps {
  FILING_INFORMATION = 'FILING_INFORMATION',
  UPLOADS = 'UPLOADS',
}

// Zod schema for form validation
const FilingFormSchema = z.object({
  filingDate: z.string().trim().min(1, 'Filing date is required'),
  taxPaid: z.string().trim().min(1, 'Tax paid is required'),
  taxLiability: z.string().trim().min(1, 'Tax liability is required'),
  taxableSales: z.string().trim().min(1, 'Taxable sales is required'),
  nonTaxableSales: z.string().trim().min(1, 'Non-taxable sales is required'),
  marketplaceSales: z.string().trim().min(1, 'Marketplace sales is required'),
  exemptSales: z.string().trim().min(1, 'Exempt sales is required'),
  grossSales: z.string().trim().min(1, 'Gross sales is required'),
  numberOfTransactions: z
    .string()
    .trim()
    .min(1, 'Number of transactions is required'),

  acknowledgementFileId: z
    .string()
    .trim()
    .min(1, 'Acknowledgement file is required'),

  acknowledgementFileUrl: z
    .string()
    .trim()
    .min(1, 'Acknowledgement file is required'),

  paymentConfirmationNumber: z
    .string()
    .trim()
    .min(1, 'Payment confirmation number is required'),
  filingConfirmationNumber: z
    .string()
    .trim()
    .min(1, 'Filing confirmation number is required'),

  paymentFileId: z.string().trim().min(1, 'Payment file is required'),
  paymentFileUrl: z.string().trim().min(1, 'Payment file is required'),

  // Adjustments
  prepayment: z.string().trim().optional(),
  priorPayment: z.string().trim().optional(),
  retailDeliveryFee: z.string().trim().optional(),
  jurisdictionCredit: z.string().trim().optional(),
  marketplacePayment: z.string().trim().optional(),
  vendorDiscount: z.string().trim().optional(),
  rounding: z.string().trim().optional(),
  penalty: z.string().trim().optional(),
  interest: z.string().trim().optional(),
});

type FilingFormData = z.infer<typeof FilingFormSchema>;

const getInitialFormValues = (
  filing?: Filing<SalesTaxFiling>
): FilingFormData => ({
  filingDate: filing?.filedAt || '',
  taxPaid: filing?.metadata?.tax_paid?.toString() || '',
  taxLiability: (
    filing?.metadata?.tax_liability ||
    filing?.metadata?.estimated_tax_collected ||
    0
  ).toFixed(2),
  taxableSales: (
    filing?.metadata?.taxable_amount ||
    filing?.metadata?.estimated_taxable_amount ||
    0
  ).toFixed(2),
  nonTaxableSales: (
    filing?.metadata?.non_taxable_amount ||
    filing?.metadata?.estimated_non_taxable_amount ||
    0
  ).toFixed(2),
  marketplaceSales: (
    filing?.metadata?.marketplace_amount ||
    filing?.metadata?.estimated_marketplace_amount ||
    0
  ).toFixed(2),
  exemptSales: (
    filing?.metadata?.exempt_amount ||
    filing?.metadata?.estimated_exempt_amount ||
    0
  ).toFixed(2),
  grossSales: (
    filing?.metadata?.gross_amount ||
    filing?.metadata?.estimated_gross_amount ||
    0
  ).toFixed(2),
  numberOfTransactions:
    filing?.metadata.filing_transactions_count?.toString() || '',
  acknowledgementFileId: '',
  acknowledgementFileUrl: '',
  paymentFileId: '',
  paymentFileUrl: '',
  prepayment: filing?.metadata.prepayment?.toString() || '',
  priorPayment: filing?.metadata.prior_payment?.toString() || '',
  retailDeliveryFee: filing?.metadata.retail_delivery_fee?.toString() || '',
  jurisdictionCredit:
    filing?.metadata.jurisdiction_issued_credit?.toString() || '',
  marketplacePayment: '0',
  vendorDiscount: filing?.metadata.vendor_discount?.toString() || '',
  rounding: filing?.metadata.rounding?.toString() || '',
  penalty: filing?.metadata.penalty?.toString() || '',
  interest: filing?.metadata.interest?.toString() || '',
  filingConfirmationNumber: filing?.metadata.filing_confirmation_number || '',
  paymentConfirmationNumber: filing?.metadata.payment_confirmation_number || '',
});

interface FilingConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  filing?: Filing<SalesTaxFiling>;
  onComplete: (data: any) => void;
  corporationId: number;
  companyId: number;
}

export function FilingConfirmationModal({
  isOpen,
  onClose,
  filing,
  onComplete,
  corporationId,
  companyId,
}: FilingConfirmationModalProps) {
  const { setNotification } = useNotification();
  const previewFilingConfirmationModalVisibility = useToggle();
  const previewPaymentConfirmationModalVisibility = useToggle();

  const [filingConfirmationFile, setFilingConfirmationFile] =
    useState<FileBasic | null>(null);
  const [filingConfirmationFileError, setFilingConfirmationFileError] =
    useState<string>('');
  const [paymentConfirmationFile, setPaymentConfirmationFile] =
    useState<FileBasic | null>(null);
  const [paymentConfirmationFileError, setPaymentConfirmationFileError] =
    useState<string>('');

  // Initialize form with react-hook-form and zod validation

  const {
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<FilingFormData>({
    resolver: zodResolver(FilingFormSchema),
    defaultValues: getInitialFormValues(filing),
  });

  useEffect(() => {
    if (isOpen) {
      reset(getInitialFormValues(filing));
    }
  }, [isOpen, filing?.id, reset]);

  const fetchSteps = () => {
    return [
      {
        id: FilingCompletionSteps.FILING_INFORMATION,
        text: 'Filing Information',
        status: TTimelineItemStatusLegacy.ACTIVE,
        isCompleted: false,
        disabled: false,
        isHidden: false,
      },
      {
        id: FilingCompletionSteps.UPLOADS,
        text: 'Uploads',
        status: TTimelineItemStatusLegacy.NOT_STARTED,
        isCompleted: false,
        disabled: false,
        isHidden: false,
      },
    ];
  };

  const { steps, setActiveStep, activeStep, markStepAsCompleted } =
    useTimelineLegacy({
      items: fetchSteps(),
    });

  const handleInputChange = (field: string, value: string) => {
    setValue(field as keyof FilingFormData, value);
  };

  const handleFormSubmit = async () => {
    const submitResponse = await submitSalesTaxFiling(
      filing?.id,
      corporationId
    );
    if (submitResponse.success) {
      setNotification({
        show: true,
        valid: true,
        title: 'Filing submitted',
        message: 'Filing submitted successfully',
      });
      mixpanel.track(MIXPANEL_EVENTS.FILING_MARKED_COMPLETE, {
        surface_area: MIXPANEL_SURFACE_AREA.AGENT_APP,
      });
      onComplete(submitResponse.data);
      onClose();
      reset();
      setFilingConfirmationFile(null);
      setPaymentConfirmationFile(null);
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to submit filing',
        message: submitResponse.error,
      });
    }
  };

  const {
    filingConfirmationFile: filingConfirmationFileResponse,
    paymentConfirmationFile: paymentConfirmationFileResponse,
    mutate: refetchDocuments,
  } = useSalesTaxFilingDocuments(filing?.id, corporationId);

  const { prepayments } = useFilingPrepaymentList({
    filingId: filing?.id,
    corporationId: corporationId.toString(),
  });

  // Load documents when UPLOADS step becomes visible
  useEffect(() => {
    if (activeStep.id === FilingCompletionSteps.UPLOADS && refetchDocuments) {
      refetchDocuments(); // Fetch documents when navigating to uploads step
    }
  }, [isOpen, activeStep.id, refetchDocuments]);

  // Set state when documents are fetched
  useEffect(() => {
    if (filingConfirmationFileResponse) {
      setFilingConfirmationFile(filingConfirmationFileResponse);
    }
  }, [filingConfirmationFileResponse]);

  useEffect(() => {
    if (paymentConfirmationFileResponse) {
      setPaymentConfirmationFile(paymentConfirmationFileResponse);
    }
  }, [paymentConfirmationFileResponse]);

  // Clear when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFilingConfirmationFile(null);
      setPaymentConfirmationFile(null);
    }
  }, [isOpen]);

  const updateFilingRecord = async (
    filingId: string,
    params?: { filedAt: boolean }
  ) => {
    const payload = {
      filing_confirmation_number: watch('filingConfirmationNumber'),
      payment_confirmation_number: watch('paymentConfirmationNumber'),
      prepayment: watch('prepayment') ? Number(watch('prepayment')) : undefined,
      prior_payment: watch('priorPayment')
        ? Number(watch('priorPayment'))
        : undefined,
      retail_delivery_fee: watch('retailDeliveryFee')
        ? Number(watch('retailDeliveryFee'))
        : undefined,
      jurisdiction_issued_credit: watch('jurisdictionCredit')
        ? Number(watch('jurisdictionCredit'))
        : undefined,
      marketplace_payment: watch('marketplacePayment')
        ? Number(watch('marketplacePayment'))
        : undefined,
      vendor_discount: watch('vendorDiscount')
        ? Number(watch('vendorDiscount'))
        : undefined,
      rounding: watch('rounding') ? Number(watch('rounding')) : undefined,
      penalty: watch('penalty') ? Number(watch('penalty')) : undefined,
      interest: watch('interest') ? Number(watch('interest')) : undefined,
      tax_paid: watch('taxPaid') ? Number(watch('taxPaid')) : undefined,
      tax_liability: watch('taxLiability')
        ? Number(watch('taxLiability'))
        : undefined,
      taxable_amount: watch('taxableSales')
        ? Number(watch('taxableSales'))
        : undefined,
      non_taxable_amount: watch('nonTaxableSales')
        ? Number(watch('nonTaxableSales'))
        : undefined,

      exempt_amount: watch('exemptSales')
        ? Number(watch('exemptSales'))
        : undefined,
      marketplace_amount: watch('marketplaceSales')
        ? Number(watch('marketplaceSales'))
        : undefined,

      ...(params?.filedAt && watch('filingDate')
        ? { filed_at: watch('filingDate') }
        : {}),
    };

    const response = await updateSalesTaxFiling(
      filingId,
      corporationId,
      payload
    );

    if (response.success) {
      setNotification({
        show: true,
        valid: true,
        title: 'Filing updated',
        message: 'Filing updated successfully',
      });
      return true;
    }

    setNotification({
      show: true,
      valid: false,
      title: 'Failed to update filing',
      message: response.error,
    });

    return false;
  };

  const handleNext = async () => {
    if (!filing?.id) {
      return;
    }

    if (activeStep.id === FilingCompletionSteps.FILING_INFORMATION) {
      // Persist filingDate at the step boundary so async refreshes/remounts
      // cannot rehydrate this modal with a blank filed_at value.
      const isUpdated = await updateFilingRecord(filing.id, { filedAt: true });
      if (isUpdated) {
        markStepAsCompleted(0);
        setActiveStep(1);
      }
    } else {
      const isUpdated = await updateFilingRecord(filing.id, { filedAt: true });
      // For the final step, submit the form
      if (isUpdated) {
        handleFormSubmit();
      }
    }
  };

  const handleFilingConfirmationFileUpload = async (fileBasic: FileBasic) => {
    setFilingConfirmationFile(fileBasic);
    setFilingConfirmationFileError('');
  };

  const handlePaymentConfirmationFileUpload = async (fileBasic: FileBasic) => {
    setPaymentConfirmationFile(fileBasic);
    setPaymentConfirmationFileError('');
  };

  const onFileUploadSuccess = async (
    filingId: string,
    file: FileBasic,
    documentType: FilingDocumentTypeEnum
  ) => {
    const response = await createFilingDocument(filingId, corporationId, {
      file,
      document_type: documentType,
    });

    if (response.success) {
      setNotification({
        show: true,
        valid: true,
        title: 'File uploaded',
        message: 'File uploaded successfully',
      });

      if (refetchDocuments) {
        await refetchDocuments();
      }

      return true;
    }
    setNotification({
      show: true,
      valid: false,
      title: 'Failed to upload file',
      message: response.error,
    });

    return false;
  };

  const onPaymentConfirmationFileUpload = async (
    file: File,
    companyId: number | undefined,
    token: string
  ) => {
    try {
      const uploadResult = await uploadFileToS3(file, companyId, token);
      if (!uploadResult) {
        throw new Error('Failed to upload file to S3');
      }

      const paymentConfirmationFileData = {
        fileName: uploadResult.fileName,
        fileUrl: uploadResult.fileUrl,
        documentType: LegacyDocumentType.Other,
      };

      await onFileUploadSuccess(
        filing?.id,
        paymentConfirmationFileData,
        FilingDocumentTypeEnum.PAYMENT_CONFIRMATION
      );

      return uploadResult;
    } catch (err) {
      setPaymentConfirmationFileError(
        'Failed to upload payment confirmation file'
      );
      throw err;
    }
  };

  const onFilingConfirmationFileUpload = async (
    file: File,
    companyId: number | undefined,
    token: string
  ) => {
    try {
      const uploadResult = await uploadFileToS3(file, companyId, token);

      if (!uploadResult) {
        throw new Error('Failed to upload file to S3');
      }

      const filingConfirmationFileData = {
        fileName: uploadResult.fileName,
        fileUrl: uploadResult.fileUrl,
        documentType: LegacyDocumentType.Other,
      };

      await onFileUploadSuccess(
        filing?.id,
        filingConfirmationFileData,
        FilingDocumentTypeEnum.FILING_CONFIRMATION
      );

      return uploadResult;
    } catch (err) {
      setFilingConfirmationFileError(
        'Failed to upload filing confirmation file'
      );
      throw err;
    }
  };

  const isFilingInformationComplete = () => {
    return (
      watch('filingDate') &&
      watch('taxPaid') &&
      watch('taxLiability') &&
      watch('taxableSales') &&
      watch('nonTaxableSales')
    );
  };

  const isFilingConfirmationComplete = () => {
    return watch('filingConfirmationNumber');
  };

  const resetFilingConfirmationFile = () => {
    setFilingConfirmationFile(null);
    setValue('acknowledgementFileId', '');
    setValue('acknowledgementFileUrl', '');
  };

  const resetPaymentConfirmationFile = () => {
    setPaymentConfirmationFile(null);
    setValue('paymentFileId', '');
    setValue('paymentFileUrl', '');
  };

  const EditActionsForFilingConfirmationFile = [
    {
      key: 'preview',
      label: 'Preview',
      icon: Icons.Eye,
      onClick: previewFilingConfirmationModalVisibility.show,
      order: 1,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: Icons.Trash01,
      onClick: resetFilingConfirmationFile,
      order: 2,
    },
  ];

  const EditActionsForPaymentConfirmationFile = [
    {
      key: 'preview',
      label: 'Preview',
      icon: Icons.Eye,
      onClick: previewPaymentConfirmationModalVisibility.show,
      order: 1,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: Icons.Trash01,
      onClick: resetPaymentConfirmationFile,
      order: 2,
    },
  ];

  const renderFilingInformation = () => (
    <div className="flex flex-col gap-6xl">
      <div className="flex flex-col gap-3xl">
        <Alert
          variant="warning"
          title="Complete Filing Information"
          description={
            <span className="txt-small-regular">
              Please enter the filing details below after successfully
              submitting the tax return to the state and use numbers from the
              state return.
            </span>
          }
        />
        <div className="flex flex-col gap-md">
          <h2 className="text-lg font-semibold mb-2xl">Filing summary</h2>
          <div className="border border-border-primary rounded-xl overflow-hidden">
            <FilingInputRow
              title="Filing date"
              value={watch('filingDate')}
              onChange={(value) => handleInputChange('filingDate', value)}
              className="border-b"
              type="date"
              error={errors.filingDate?.message}
              required
            />

            <FilingInputRow
              title="Tax Liability"
              value={watch('taxLiability')}
              onChange={(value) => handleInputChange('taxLiability', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.taxLiability?.message}
              required
              editable={false}
            />

            <FilingInputRow
              title="Tax paid"
              value={watch('taxPaid')}
              onChange={(value) => handleInputChange('taxPaid', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.taxPaid?.message}
              required
            />

            <FilingInputRow
              title="Gross sales"
              value={watch('grossSales')}
              onChange={(value) => handleInputChange('grossSales', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.grossSales?.message}
              required
              editable={false}
            />

            <FilingInputRow
              title="Taxable sales"
              value={watch('taxableSales')}
              onChange={(value) => handleInputChange('taxableSales', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.taxableSales?.message}
              required
              editable={false}
            />

            <FilingInputRow
              title="Non-taxable sales"
              value={watch('nonTaxableSales')}
              onChange={(value) => handleInputChange('nonTaxableSales', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.nonTaxableSales?.message}
              required
              editable={false}
            />

            <FilingInputRow
              title="Exempt sales"
              value={watch('exemptSales')}
              onChange={(value) => handleInputChange('exemptSales', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.exemptSales?.message}
              required
              editable={false}
            />

            <FilingInputRow
              title="Marketplace sales"
              value={watch('marketplaceSales')}
              onChange={(value) => handleInputChange('marketplaceSales', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.marketplaceSales?.message}
              required
              editable={false}
            />

            <FilingInputRow
              title="Number of transactions"
              value={watch('numberOfTransactions')}
              onChange={(value) =>
                handleInputChange('numberOfTransactions', value)
              }
              placeholder="Enter number"
              type="number"
              error={errors.numberOfTransactions?.message}
              required
              editable={false}
            />
          </div>
        </div>

        <div className="flex flex-col gap-md">
          <h2 className="text-lg font-semibold mb-2xl">
            Filing prepayment logs
          </h2>
          <PrepaymentLogTable
            prepayments={prepayments}
            corporationId={corporationId}
          />
        </div>

        <div className="flex flex-col gap-md">
          <h2 className="text-lg font-semibold mb-2xl">Adjustments</h2>
          <div className="border border-border-primary rounded-xl overflow-hidden">
            <FilingInputRow
              title="Prepayment"
              value={watch('prepayment')}
              onChange={(value) => handleInputChange('prepayment', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.prepayment?.message}
              hint="+ve only"
            />

            <FilingInputRow
              title="Prior payment"
              value={watch('priorPayment')}
              onChange={(value) => handleInputChange('priorPayment', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.priorPayment?.message}
              hint="+ve only"
            />

            <FilingInputRow
              title="Retail delivery fee"
              value={watch('retailDeliveryFee')}
              onChange={(value) =>
                handleInputChange('retailDeliveryFee', value)
              }
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.retailDeliveryFee?.message}
              hint="+ve only"
            />

            <FilingInputRow
              title="Jurisdiction credit"
              value={watch('jurisdictionCredit')}
              onChange={(value) =>
                handleInputChange('jurisdictionCredit', value)
              }
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.jurisdictionCredit?.message}
              hint="+ve only"
            />

            <FilingInputRow
              title="Vendor discount"
              value={watch('vendorDiscount')}
              onChange={(value) => handleInputChange('vendorDiscount', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.vendorDiscount?.message}
              hint="+ve only"
            />

            <FilingInputRow
              title="Rounding"
              value={watch('rounding')}
              onChange={(value) => handleInputChange('rounding', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.rounding?.message}
              hint="+ve/-ve"
            />

            <FilingInputRow
              title="Penalty"
              value={watch('penalty')}
              onChange={(value) => handleInputChange('penalty', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.penalty?.message}
              hint="+ve only"
            />

            <FilingInputRow
              title="Interest"
              value={watch('interest')}
              onChange={(value) => handleInputChange('interest', value)}
              placeholder="Enter amount"
              prefix="$"
              type="number"
              error={errors.interest?.message}
              hint="+ve only"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderUploads = () => (
    <div className="flex flex-col gap-6">
      <h2 className="text-xl font-semibold">Uploads</h2>

      <div className="flex flex-col gap-sm">
        <div className="txt-small-medium text-text-secondary">
          Filing acknowledgement
        </div>

        <div className="flex flex-col gap-2">
          <SingleFileUploader
            file={filingConfirmationFile}
            setFile={setFilingConfirmationFile}
            onFileChange={handleFilingConfirmationFileUpload}
            onFileUpload={onFilingConfirmationFileUpload}
            onError={() => { }}
            companyId={companyId}
            token={undefined}
            error={filingConfirmationFileError}
            setError={setFilingConfirmationFileError}
            documentType={LegacyDocumentType.Other}
            maxSize={FILING_CONFIRMATION_FILE_CONFIG.maxSize}
            accept={FILING_CONFIRMATION_FILE_CONFIG.acceptedTypes}
            message={FILING_CONFIRMATION_FILE_CONFIG.message}
            fileActions={EditActionsForFilingConfirmationFile}
          />
          {errors?.acknowledgementFileUrl?.message && (
            <InputErrorMessage
              message={errors?.acknowledgementFileUrl?.message}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col gap-sm">
        <div className="txt-small-medium text-text-secondary">
          Payment confirmation
        </div>

        <div className="flex flex-col gap-2">
          <SingleFileUploader
            file={paymentConfirmationFile}
            setFile={setPaymentConfirmationFile}
            onFileChange={handlePaymentConfirmationFileUpload}
            onFileUpload={onPaymentConfirmationFileUpload}
            onError={() => { }}
            token={undefined}
            error={paymentConfirmationFileError}
            companyId={companyId}
            setError={setPaymentConfirmationFileError}
            documentType={LegacyDocumentType.Other}
            maxSize={PAYMENT_CONFIRMATION_FILE_CONFIG.maxSize}
            accept={PAYMENT_CONFIRMATION_FILE_CONFIG.acceptedTypes}
            message={PAYMENT_CONFIRMATION_FILE_CONFIG.message}
            fileActions={EditActionsForPaymentConfirmationFile}
          />
          {errors?.paymentFileUrl?.message && (
            <InputErrorMessage message={errors?.paymentFileUrl?.message} />
          )}
        </div>
      </div>
      <div className="flex flex-col gap-sm">
        <div className="txt-small-medium text-text-secondary">
          Filing confirmation
        </div>
        <div className="border border-border-primary rounded-xl overflow-hidden">
          <FilingInputRow
            title="Acknowledgement"
            value={watch('filingConfirmationNumber')}
            onChange={(value) =>
              handleInputChange('filingConfirmationNumber', value)
            }
            placeholder="Enter acknowledgement number"
            type="text"
            className="border-b"
            error={errors.filingConfirmationNumber?.message}
            required
          />

          <FilingInputRow
            title="Payment confirmation"
            value={watch('paymentConfirmationNumber')}
            onChange={(value) =>
              handleInputChange('paymentConfirmationNumber', value)
            }
            placeholder="Enter payment confirmation number"
            type="text"
            error={errors.paymentConfirmationNumber?.message}
          />
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex flex-col min-w-[90vw] h-[90vh]"
        preventCloseOnOutsideClick
        hideCloseIcon
      >
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="flex gap-2 items-center">
            Complete {filing?.filingName}
            <span className="txt-small-regular text-text-tertiary">
              | {filing?.corporation?.companyDisplayName} (
              {filing?.filingPeriod})
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="flex h-full">
            <div className="w-[280px] border-r border-border-primary bg-bg-tertiary p-6">
              <TimelineLegacy steps={steps} setActiveStep={setActiveStep} />
            </div>

            <div className="flex-1 p-8 overflow-y-auto">
              {activeStep.id === FilingCompletionSteps.FILING_INFORMATION &&
                renderFilingInformation()}
              {activeStep.id === FilingCompletionSteps.UPLOADS &&
                renderUploads()}
            </div>
          </div>
        </div>

        <FilePreviewModal
          isOpen={previewFilingConfirmationModalVisibility.visible}
          onClose={previewFilingConfirmationModalVisibility.hide}
          fileUrl={filingConfirmationFile?.signedUrl}
          title={filingConfirmationFile?.fileName}
        />

        <FilePreviewModal
          isOpen={previewPaymentConfirmationModalVisibility.visible}
          onClose={previewPaymentConfirmationModalVisibility.hide}
          fileUrl={paymentConfirmationFile?.signedUrl}
          title={paymentConfirmationFile?.fileName}
        />

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleNext}
            disabled={
              (activeStep.id === FilingCompletionSteps.FILING_INFORMATION &&
                !isFilingInformationComplete()) ||
              (activeStep.id === FilingCompletionSteps.UPLOADS &&
                (!filingConfirmationFile ||
                  !isFilingInformationComplete() ||
                  !isFilingConfirmationComplete()))
            }
          >
            {activeStep.id === FilingCompletionSteps.UPLOADS
              ? 'Complete'
              : 'Next'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
